// ═══════════════════════════════════════════════════════════════════════════════
// Monthly recurrence subtype controls (day / nth weekday / last weekday)
// ═══════════════════════════════════════════════════════════════════════════════

import { MONTHLY_KIND, type MonthlyKind } from '@fdh/shared';

const DAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const DAY_KEYS = [
  'task.day.sun',
  'task.day.mon',
  'task.day.tue',
  'task.day.wed',
  'task.day.thu',
  'task.day.fri',
  'task.day.sat',
] as const;
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const ORDINALS = [1, 2, 3, 4, 5] as const;

export type MonthlySchedulePatch = {
  monthlyKind: MonthlyKind;
  recurrenceDays: number[];
  recurrenceMonthDays: number[];
  recurrenceOrdinal: number | null;
};

type TaskMonthlyScheduleControlsProps = {
  t: (key: string) => string;
  monthlyKind: MonthlyKind | null;
  recurrenceDays: number[];
  recurrenceMonthDays: number[];
  recurrenceOrdinal: number | null;
  onChange: (patch: MonthlySchedulePatch) => void;
  idPrefix?: string;
};

export const emptyMonthlySchedulePatch = (
  kind: MonthlyKind = MONTHLY_KIND.DAY,
): MonthlySchedulePatch => {
  if (kind === MONTHLY_KIND.DAY) {
    return {
      monthlyKind: MONTHLY_KIND.DAY,
      recurrenceDays: [],
      recurrenceMonthDays: [],
      recurrenceOrdinal: null,
    };
  }
  if (kind === MONTHLY_KIND.NTH_WEEKDAY) {
    return {
      monthlyKind: MONTHLY_KIND.NTH_WEEKDAY,
      recurrenceDays: [],
      recurrenceMonthDays: [],
      recurrenceOrdinal: 1,
    };
  }
  return {
    monthlyKind: MONTHLY_KIND.LAST_WEEKDAY,
    recurrenceDays: [],
    recurrenceMonthDays: [],
    recurrenceOrdinal: null,
  };
};

export const TaskMonthlyScheduleControls = ({
  t,
  monthlyKind,
  recurrenceDays,
  recurrenceMonthDays,
  recurrenceOrdinal,
  onChange,
  idPrefix = 'monthly',
}: TaskMonthlyScheduleControlsProps) => {
  const kind = monthlyKind ?? MONTHLY_KIND.DAY;
  const showClampTip = kind === MONTHLY_KIND.DAY && recurrenceMonthDays.some((d) => d >= 29);
  const showFifthTip = kind === MONTHLY_KIND.NTH_WEEKDAY && recurrenceOrdinal === 5;

  const handleKindChange = (nextKind: MonthlyKind) => {
    onChange(emptyMonthlySchedulePatch(nextKind));
  };

  const handleMonthDayToggle = (day: number) => {
    const next = recurrenceMonthDays.includes(day)
      ? recurrenceMonthDays.filter((d) => d !== day)
      : [...recurrenceMonthDays, day].sort((a, b) => a - b);
    onChange({
      monthlyKind: MONTHLY_KIND.DAY,
      recurrenceDays: [],
      recurrenceMonthDays: next,
      recurrenceOrdinal: null,
    });
  };

  const handleWeekdayToggle = (day: number) => {
    const next = recurrenceDays.includes(day)
      ? recurrenceDays.filter((d) => d !== day)
      : [...recurrenceDays, day].sort((a, b) => a - b);
    onChange({
      monthlyKind: kind,
      recurrenceDays: next,
      recurrenceMonthDays: [],
      recurrenceOrdinal: kind === MONTHLY_KIND.NTH_WEEKDAY ? (recurrenceOrdinal ?? 1) : null,
    });
  };

  const handleOrdinalChange = (ordinal: number) => {
    onChange({
      monthlyKind: MONTHLY_KIND.NTH_WEEKDAY,
      recurrenceDays,
      recurrenceMonthDays: [],
      recurrenceOrdinal: ordinal,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label
          className="text-sm font-medium text-gray-700 dark:text-gray-200"
          htmlFor={`${idPrefix}-kind`}
        >
          {t('task.form.monthlyKindLabel')}
        </label>
        <select
          id={`${idPrefix}-kind`}
          value={kind}
          onChange={(e) => handleKindChange(e.target.value as MonthlyKind)}
          className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value={MONTHLY_KIND.DAY}>{t('task.monthlyKind.day')}</option>
          <option value={MONTHLY_KIND.NTH_WEEKDAY}>{t('task.monthlyKind.nthWeekday')}</option>
          <option value={MONTHLY_KIND.LAST_WEEKDAY}>{t('task.monthlyKind.lastWeekday')}</option>
        </select>
      </div>

      {kind === MONTHLY_KIND.DAY ? (
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t('task.form.monthDaysLabel')}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {MONTH_DAYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => handleMonthDayToggle(day)}
                className={`flex h-10 min-w-10 items-center justify-center rounded-full px-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  recurrenceMonthDays.includes(day)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
                }`}
                aria-pressed={recurrenceMonthDays.includes(day)}
                aria-label={`${t('task.form.monthDaysLabel')} ${day}`}
              >
                {day}
              </button>
            ))}
          </div>
          {showClampTip ? (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300" role="note">
              {t('task.form.monthlyDayClampHint')}
            </p>
          ) : null}
        </div>
      ) : null}

      {kind === MONTHLY_KIND.NTH_WEEKDAY ? (
        <div>
          <label
            className="text-sm font-medium text-gray-700 dark:text-gray-200"
            htmlFor={`${idPrefix}-ordinal`}
          >
            {t('task.form.monthlyOrdinalLabel')}
          </label>
          <select
            id={`${idPrefix}-ordinal`}
            value={recurrenceOrdinal ?? 1}
            onChange={(e) => handleOrdinalChange(Number(e.target.value))}
            className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            {ORDINALS.map((n) => (
              <option key={n} value={n}>
                {t(`task.form.monthlyOrdinal.${n}`)}
              </option>
            ))}
          </select>
          {showFifthTip ? (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300" role="note">
              {t('task.form.monthlyFifthWeekdayHint')}
            </p>
          ) : null}
        </div>
      ) : null}

      {kind === MONTHLY_KIND.NTH_WEEKDAY || kind === MONTHLY_KIND.LAST_WEEKDAY ? (
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t('task.form.daysLabel')}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => handleWeekdayToggle(day)}
                className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  recurrenceDays.includes(day)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
                }`}
                aria-pressed={recurrenceDays.includes(day)}
              >
                {t(DAY_KEYS[day])}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
